import {
  Box,
  Stack,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Button,
  alpha,
  Divider,
  Collapse,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState, useRef } from 'react';

import { PostResponse, useGetReplies } from '@/api/endpoints/community/posts';
import { ChevronDownIcon } from '@/assets/icons/ChevronDownIcon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { HeartIcon } from '@/assets/icons/HeartIcon';
import { Pin01Icon } from '@/assets/icons/Pin01Icon';
import { Send01Icon } from '@/assets/icons/Send01Icon';
import { useAuth } from '@/context/AuthProvider';

import { ReplyFormData } from './ReplyForm';
import { ReplyThread } from './ReplyThread';

interface PostCardProps {
  post: PostResponse;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleLike?: () => void;
  onCreateReply?: (data: ReplyFormData) => Promise<void>;
  onToggleReplyLike?: (replyId: number) => Promise<void>;
  onDeleteReply?: (replyId: number) => Promise<void>;
  showActions?: boolean;
  isUserMember?: boolean;
  isUserAdmin?: boolean;
  createReplyLoading?: boolean;
}

export const PostCard: React.FC<PostCardProps> = ({
  post,
  onEdit,
  onDelete,
  onToggleLike,
  onCreateReply,
  onToggleReplyLike,
  onDeleteReply,
  showActions = true,
  isUserMember = false,
  isUserAdmin = false,
  createReplyLoading = false,
}) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [commentsCollapsed, setCommentsCollapsed] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentFocused, setCommentFocused] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const menuOpen = Boolean(anchorEl);

  const isAuthor = user?.id === post.author.user.id;
  const canEdit = isAuthor || isUserAdmin;
  const canDelete = isAuthor || isUserAdmin;

  // Load replies when there are comments
  const { data: replies = [], isLoading: repliesLoading } = useGetReplies(
    { postId: post.id, page: 1, limit: 50 },
    true
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.();
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.();
  };

  const handleCommentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!commentText.trim() || !onCreateReply) return;

    await onCreateReply({
      content: commentText.trim(),
    });

    setCommentText('');
    setCommentsCollapsed(false);
  };

  const handleCommentKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleCommentSubmit(event);
    }
  };

  const handleReplyToComment = async (data: ReplyFormData) => {
    if (onCreateReply) {
      await onCreateReply(data);
    }
  };

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    return {
      children: initials,
    };
  };

  const getUserDisplayName = () => user?.user_metadata?.full_name || user?.email || 'User';

  // Sort replies by likes and get top 2 for preview
  const sortedReplies = replies.sort((a, b) => (b._count?.likes || 0) - (a._count?.likes || 0));
  const topReplies = sortedReplies.slice(0, 2);
  const hasMoreComments = replies.length > 2;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: alpha('#9D1500', 0.2),
          boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.1)',
        },
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2, pt: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
              <Avatar
                {...getAvatarProps(post.author.user.full_name || post.author.user.email)}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  color: 'white',
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: 'secondary.900', mt: 1 }}
                  >
                    {post.author.user.full_name || post.author.user.email}
                  </Typography>
                  {post.is_pinned && (
                    <Chip
                      icon={<Pin01Icon />}
                      label="Pinned"
                      size="small"
                      sx={{
                        height: 24,
                        px: 0.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.025em',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(157, 21, 0, 0.2)',
                        transition: 'all 0.2s ease-in-out',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%': {
                            transform: 'scale(1)',
                          },
                          '50%': {
                            transform: 'scale(1.02)',
                          },
                          '100%': {
                            transform: 'scale(1)',
                          },
                        },
                        '& .MuiChip-icon': {
                          fontSize: '0.875rem',
                          color: 'white',
                          marginLeft: '6px',
                          marginRight: '-2px',
                        },
                        '& .MuiChip-label': {
                          px: 1,
                        },
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          boxShadow: '0 3px 6px rgba(157, 21, 0, 0.3)',
                          animation: 'none',
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="secondary.600">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                  {post.updated_at !== post.created_at && ' • edited'}
                </Typography>
              </Box>
            </Stack>

            {showActions && (canEdit || canDelete) && (
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{
                  color: 'secondary.500',
                  mt: 1,
                  '&:hover': {
                    bgcolor: 'secondary.100',
                  },
                }}
              >
                <DotsVerticalIcon />
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, pb: 2 }}>
          {post.title && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'secondary.900',
                mb: 1,
                lineHeight: 1.3,
              }}
            >
              {post.title}
            </Typography>
          )}
          {post.content && (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: 'secondary.700',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {isContentExpanded || post.content.length <= 120
                  ? post.content
                  : `${post.content.substring(0, 120)}...`}
              </Typography>
              {post.content.length > 120 && (
                <Button
                  onClick={() => setIsContentExpanded(!isContentExpanded)}
                  sx={{
                    mt: 0.5,
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {isContentExpanded ? 'Show less' : 'Read more'}
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Document Attachments */}
        {post.documentTags && post.documentTags.length > 0 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'secondary.700' }}>
              Attachments ({post.documentTags.length})
            </Typography>
            <Stack spacing={1}>
              {post.documentTags.map(docTag => (
                <Box
                  key={docTag.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'secondary.200',
                    bgcolor: 'secondary.50',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'secondary.900' }}>
                    {docTag.document.original_name}
                  </Typography>
                  <Typography variant="caption" color="secondary.600">
                    {docTag.document.mime_type} • {Math.round(docTag.document.size / 1024)} KB
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Divider />

        {/* Action Buttons - Facebook Style */}
        <Box sx={{ px: 3, py: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {/* Like Button - Left Side */}
            <Button
              startIcon={
                <HeartIcon
                  sx={{
                    '& path': {
                      fill: post.is_liked ? '#f44336' : 'none',
                      stroke: post.is_liked ? '#f44336' : 'currentColor',
                    },
                  }}
                />
              }
              size="medium"
              onClick={onToggleLike}
              disabled={!isUserMember}
              sx={{
                color: post.is_liked ? 'error.main' : 'secondary.600',
                py: 0.75,
                px: 2,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'transparent',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: 'transparent',
                },
                '&:focus': {
                  bgcolor: 'transparent',
                  outline: 'none',
                },
                '&:active': {
                  bgcolor: 'transparent',
                  outline: 'none',
                },
                '&:disabled': {
                  color: 'secondary.400',
                },
              }}
            >
              {post._count.likes > 0 ? `${post._count.likes} ` : ''}Like
            </Button>

            {/* Comment Input - Right Side */}
            {isUserMember && (
              <Box sx={{ width: '80%' }}>
                <form onSubmit={handleCommentSubmit}>
                  <TextField
                    ref={commentInputRef}
                    placeholder={`Comment as ${getUserDisplayName()}`}
                    multiline
                    maxRows={3}
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyPress={handleCommentKeyPress}
                    onFocus={() => setCommentFocused(true)}
                    onBlur={() => setCommentFocused(false)}
                    disabled={createReplyLoading}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: 'secondary.50',
                        border: '1px solid',
                        borderColor: commentFocused ? 'primary.main' : 'secondary.200',
                        '&:hover': {
                          borderColor: commentFocused ? 'primary.main' : 'secondary.300',
                        },
                        '&.Mui-focused': {
                          borderColor: 'primary.main',
                        },
                        minHeight: '36px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '0.875rem',
                        py: 0.25,
                        px: 1.25,
                      },
                    }}
                    InputProps={{
                      endAdornment: commentText.trim() && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleCommentSubmit}
                            disabled={createReplyLoading}
                            sx={{
                              color: 'primary.main',
                              width: 24,
                              height: 24,
                              '&:hover': {
                                bgcolor: alpha('#9D1500', 0.1),
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            }}
                          >
                            <Send01Icon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Comments List */}
        {replies.length > 0 && !commentsCollapsed && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 1.5 }}>
              <Collapse in={replies.length > 0 && !commentsCollapsed} timeout={300}>
                <Stack spacing={1}>
                  {/* Comments List */}
                  {repliesLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {!repliesLoading && replies.length > 0 && (
                    <>
                      <ReplyThread
                        replies={showAllComments ? replies : topReplies}
                        onReply={handleReplyToComment}
                        onToggleLike={onToggleReplyLike}
                        onDelete={onDeleteReply}
                        isUserMember={isUserMember}
                        isUserAdmin={isUserAdmin}
                      />

                      {/* View More/Less Button */}
                      {hasMoreComments && (
                        <Button
                          onClick={() => setShowAllComments(!showAllComments)}
                          sx={{
                            color: 'secondary.600',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            minWidth: 'auto',
                            p: 0,
                            '&:hover': {
                              bgcolor: 'transparent',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {showAllComments ? (
                            <>
                              Close comments
                              <ChevronDownIcon
                                sx={{
                                  ml: 0.5,
                                  transform: 'rotate(180deg)',
                                  fontSize: '0.875rem',
                                }}
                              />
                            </>
                          ) : (
                            <>
                              View {replies.length - 2} more comments
                              <ChevronDownIcon
                                sx={{
                                  ml: 0.5,
                                  fontSize: '0.875rem',
                                }}
                              />
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              </Collapse>
            </Box>
          </>
        )}
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 120,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
            },
          },
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit} sx={{ fontSize: '0.875rem' }}>
            Edit Post
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              fontSize: '0.875rem',
              color: 'error.main',
              '&:hover': {
                bgcolor: alpha('#f44336', 0.08),
              },
            }}
          >
            Delete Post
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};
